import Card from "common/Boxes/Card";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { BotGraphType, BotType } from "@shared/types";
import styled, { useTheme } from "styled-components";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Text from "common/Typography/Text";
import useMeasure from "utils/hooks/useMeasure";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import TextWithIcon from "common/Text/TextWithIcon";
import BotsGraphCard from "features/bots/MyBots/BotsGraphCard";
import BotsGraphTabs from "features/bots/MyBots/BotsGraphTabs";
import { useMyBotsStatistics } from "@shared/features/bots/hooks";
import StatsAndChartCard from "common/Boxes/StatsAndChartCard.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { ICON_TYPES } from "@shared/common/constants.ts";
import { hexToRGB } from "@shared/utils/misc.ts";
import { MOBILE_LAYOUT } from "features/bots/common/constants.ts";
import {
  EXPANDED_CHART_HEIGHT,
  TABS_HEIGHT,
  TIME_RANGE_BUTTONS_HEIGHT,
} from "features/dashboard/MobileDashboard/ExpandingChart.tsx";

interface BotsStatisticsProps {
  botType: BotType;
  isLoading?: boolean;
}

const BotsStatistics = forwardRef(
  ({ botType, isLoading }: BotsStatisticsProps, ref) => {
    const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
    const theme = useTheme();
    const [measureRef, { width }] = useMeasure<HTMLDivElement>();
    const { isFetching, numClosedData, profitData } =
      useMyBotsStatistics(botType);
    const isMobile = useMedia(
      [`(max-width: ${MOBILE_LAYOUT}px)`],
      [true],
      false
    );
    const useTabs = width < 1000;
    const [expanded, setExpanded] = useState(false);
    const chartRef1 = useRef<{ refetch?: () => void }>(undefined);
    const chartRef2 = useRef<{ refetch?: () => void }>(undefined);

    useImperativeHandle(ref, () => ({
      refetch: () => {
        try {
          chartRef1.current?.refetch?.();
        } catch {}
        try {
          chartRef2.current?.refetch?.();
        } catch {}
      },
    }));

    const getCharts = () => {
      if (!useTabs) {
        return (
          <EqualColumnsGrid numColumns={2} style={{ flex: "auto" }}>
            <BotsGraphCard
              title={t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT])}
              botType={botType}
              graphType={BotsGraphDataKeys.ACCUMULATING_PROFIT}
              ref={chartRef1}
              isLoading={isLoading}
            />
            <BotsGraphCard
              title={t(BotGraphTypeToDisplay[BotGraphType.PROFIT_BY_DAY])}
              botType={botType}
              graphType={BotsGraphDataKeys.PROFIT_BY_DAY}
              useBarChart={true}
              ref={chartRef2}
              isLoading={isLoading}
            />
          </EqualColumnsGrid>
        );
      } else {
        return (
          <BotsGraphTabs
            botType={botType}
            expanded={expanded}
            ref={chartRef1}
          />
        );
      }
    };

    const profitStatistic = (
      <Statistic $expanded={expanded}>
        <StatisticTitle>{profitData.title}</StatisticTitle>
        <StatisticValue as="div" type={profitData.type}>
          <TextWithIcon
            text={profitData.value}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            iconStyle={
              isMobile
                ? {
                    fontSize: theme.typography.fontSizes.L,
                    color: theme.isNewLook
                      ? theme.colors.secondaryText
                      : hexToRGB(theme.colors.white, 0.5),
                    marginRight: 2,
                  }
                : {}
            }
          />
        </StatisticValue>
      </Statistic>
    );

    const closedTradesStatistic = (
      <Statistic $expanded={expanded}>
        <StatisticTitle>{numClosedData.title}</StatisticTitle>
        <StatisticValue>{numClosedData.value}</StatisticValue>
      </Statistic>
    );

    if (isMobile) {
      return (
        <StatsAndChartCard
          topStatistic={profitStatistic}
          bottomStatistic={closedTradesStatistic}
          chart={getCharts()}
          expanded={expanded}
          setExpanded={setExpanded}
          statisticsStyle={{ alignItems: "flex-start" }}
          expandedHeight={
            EXPANDED_CHART_HEIGHT + TIME_RANGE_BUTTONS_HEIGHT + TABS_HEIGHT + 16
          }
        />
      );
    }

    return (
      <Wrapper ref={measureRef} id="my-bots-statistics">
        <StatisticsWrapper>
          <LoadingSpinnerOverlay
            isLoading={isFetching}
            backgroundColor={theme.colors.cardBgColor}
          >
            {closedTradesStatistic}
            {profitStatistic}
          </LoadingSpinnerOverlay>
        </StatisticsWrapper>
        {getCharts()}
      </Wrapper>
    );
  }
);

export default React.memo(BotsStatistics);

export enum BotsGraphDataKeys {
  ACCUMULATING_PROFIT = "accumulatedProfit",
  PROFIT_BY_DAY = "profitByDay",
}

const Wrapper = styled.div`
  display: flex;
  column-gap: 16px;
  @media (max-width: ${MOBILE_LAYOUT}px) {
    flex-direction: column;
    row-gap: 16px;
  }
`;
const StatisticsWrapper = styled(Card)`
  flex: 0 0 250px;
  position: relative;
  height: auto;
  @media (max-width: ${MOBILE_LAYOUT}px) {
    display: flex;
    flex: 0 0 50px;
    padding: 12px;
  }
`;
const Statistic = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50%;
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.light};
  }
  @media (max-width: ${MOBILE_LAYOUT}px) {
    &:not(:last-of-type) {
      border-bottom: none;
      width: ${(p) => (p.$expanded ? "50%" : "100%")};
    }
    justify-content: flex-start;
    align-items: flex-start;
  }
`;
const StatisticTitle = styled(Text).attrs({
  type: "secondary",
})`
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.XL};
  @media (max-width: ${MOBILE_LAYOUT}px) {
    font-size: ${(p) => p.theme.typography.fontSizes.S};
    color: ${(p) =>
      p.theme.isNewLook ? p.theme.colors.titleColor : p.theme.colors.white};
  }
`;
const StatisticValue = styled(Text)`
  line-height: 40px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.XXL};
  @media (max-width: ${MOBILE_LAYOUT}px) {
    font-size: 22px;
    line-height: 28px;
    color: ${(p) =>
      p.theme.isNewLook ? p.theme.colors.titleColor : p.theme.colors.white};
  }
`;
